/* eslint-disable no-console */
import { Injectable } from '@angular/core';
import {
  Observable, map
} from 'rxjs';

import { ConfigurationService } from '@sondermind/configuration';
import { IAge, ITreatmentPreference } from '@sondermind/data-access/public-facing/filter-form';
import { HttpService } from '@sondermind/http';
import { IAPIListRequestOptions, IAPIListResponse } from '@sondermind/utilities/models-http';
import { camelCaseTransformer } from '@sondermind/utilities/tools';

import { IGender, IGenderApiResponse } from '../models/gender';
import { IModality, IModalityApiResponse } from '../models/modality';
import { ISpecialty, ISpecialtyApiResponse } from '../models/specialty';

@Injectable({
  providedIn: 'root'
})
export class FilterOptionsHttpService {
  constructor(
    private configurationService: ConfigurationService,
    private http: HttpService,
  ) { }

  listGenders$(opts: Partial<IAPIListRequestOptions> = {}): Observable<IAPIListResponse<IGender>> {
    const params = this.http.generateListParams(opts);
    return this.http
      .get<IAPIListResponse<IGenderApiResponse>>(this.fetchUrlGenders, params)
      .pipe(
        map((response: IAPIListResponse<IGenderApiResponse>) => (
          {
            ...response,
            data: response.data.map(
              (genderApiResponse: IGenderApiResponse) => camelCaseTransformer<IGender>(genderApiResponse)
            )
          }
        )),
        this.http.handleHttpErrorStrict()
      );
  }

  listSpecialties$(opts: Partial<IAPIListRequestOptions> = {}): Observable<IAPIListResponse<ISpecialty>> {
    const params = this.http.generateListParams(opts);
    return this.http
      .get<IAPIListResponse<ISpecialtyApiResponse>>(this.fetchUrlSpecialties, params)
      .pipe(
        map((response: IAPIListResponse<ISpecialtyApiResponse>) => (
          {
            ...response,
            data: response.data.map(
              (concernApiResponse: ISpecialtyApiResponse) => camelCaseTransformer<ISpecialty>(concernApiResponse)
            )
          }
        )),
        this.http.handleHttpErrorStrict()
      );
  }

  listModalities$(opts: Partial<IAPIListRequestOptions> = {}): Observable<IAPIListResponse<IModality>> {
    const params = this.http.generateListParams(opts);

    return this.http
      .get<IAPIListResponse<IModalityApiResponse>>(this.fetchUrlModalities, params)
      .pipe(
        map((response: IAPIListResponse<IModalityApiResponse>) => (
          {
            ...response,
            data: response.data.map(
              (modaltiyApiResponse: IModalityApiResponse) => camelCaseTransformer<IModality>(modaltiyApiResponse)
            )
          }
        )),
        this.http.handleHttpErrorStrict()
      );
  }

  listTreatmentPreferences$(opts: Partial<IAPIListRequestOptions> = {}): Observable<IAPIListResponse<ITreatmentPreference>> {
    const params = this.http.generateListParams(opts);

    return this.http
      .get<IAPIListResponse<ITreatmentPreference>>(this.fetchUrlTreatmentPreferences, params)
      .pipe(
        map((response: IAPIListResponse<ITreatmentPreference>) => (
          {
            ...response,
            data: response.data.map(
              (apiResponse: ITreatmentPreference) => camelCaseTransformer<ITreatmentPreference>(apiResponse)
            )
          }
        )),
        this.http.handleHttpErrorStrict()
      );
  }

  listAges$(opts: Partial<IAPIListRequestOptions> = {}): Observable<IAPIListResponse<IAge>> {
    const params = this.http.generateListParams(opts);

    return this.http
      .get<IAPIListResponse<IAge>>(this.fetchUrlAges, params)
      .pipe(
        map((response: IAPIListResponse<IAge>) => (
          {
            ...response,
            data: response.data.map(
              (apiResponse: IAge) => camelCaseTransformer<IAge>(apiResponse)
            )
          }
        )),
        this.http.handleHttpErrorStrict()
      );
  }

  // to do: add root route to environment files.
  get fetchUrlGenders(): string {
    return `${this.configurationService.env.enginelessBase}/reference_datum/genders`;
  }

  get fetchUrlProviderNetworkCompanies(): string {
    return `${this.configurationService.env.svcBase}/flows/provider_network_companies`;
  }

  get fetchUrlSpecialties(): string {
    return `${this.configurationService.env.enginelessBase}/reference_datum/specialties`;
  }

  get fetchUrlModalities(): string {
    return `${this.configurationService.env.enginelessBase}/reference_datum/modalities`;
  }

  get fetchUrlStates(): string {
    return `${this.configurationService.env.svcBase}/billing/provider_network_company_states`;
  }

  get fetchUrlTreatmentPreferences(): string {
    return `${this.configurationService.env.enginelessBase}/reference_datum/treatment_preferences`;
  }

  get fetchUrlAges(): string {
    return `${this.configurationService.env.enginelessBase}/reference_datum/ages`;
  }
}
